import { isString, verify, ApiFactory } from '@tradingblock/api';
import { LoginJwtValue, UIClientTypes } from '@tradingblock/types';
import { Config } from '../config';
import jwt from 'jsonwebtoken';

type ValidTokenType = {
  value: string;
  decoded: LoginJwtValue;
};

const PUBLIC_KEY_SESSION_PREFIX = 'tradingblock.uiApi.publicKey.v1';

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

function sessionStorageKeyForUiApi(): string {
  return `${PUBLIC_KEY_SESSION_PREFIX}:${encodeURIComponent(Config.uiApi)}`;
}

/** Clears cached UI API JWKS public key for this tab (e.g. after key rotation). */
export const clearUiApiPublicKeyCache = (): void => {
  try {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.removeItem(sessionStorageKeyForUiApi());
  } catch {
    /* ignore */
  }
};

/**
 * Fetches the RS256 public key for JWT verification, with per-tab sessionStorage cache.
 * @param forceRefresh When true, skips cache and refetches (e.g. after verify failure).
 */
export const getUiApiPublicKey = async (forceRefresh = false): Promise<string> => {
  const storageKey = sessionStorageKeyForUiApi();

  if (!forceRefresh && typeof sessionStorage !== 'undefined') {
    const raw = sessionStorage.getItem(storageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { key: string };
        if (parsed.key && typeof parsed.key === 'string') {
          return parsed.key;
        }
      } catch {
        sessionStorage.removeItem(storageKey);
      }
    }
  }

  const res = await fetch(`${Config.uiApi}/publickey`);
  if (!res.ok) {
    throw new Error(`publickey_request_failed: ${res.status}`);
  }
  const pubKey = await res.json();
  if (!pubKey || typeof pubKey.key !== 'string') {
    throw new Error('publickey_response_invalid');
  }

  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(storageKey, JSON.stringify({ key: pubKey.key }));
    }
  } catch {
    /* storage quota or disabled */
  }

  return pubKey.key;
};

async function getUiApiPublicKeyWithRetries(maxAttempts: number): Promise<string> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const forceRefresh = attempt > 0;
      return await getUiApiPublicKey(forceRefresh);
    } catch (e) {
      lastErr = e;
      console.warn(`[auth] getUiApiPublicKey attempt ${attempt + 1}/${maxAttempts} failed`, e);
      clearUiApiPublicKeyCache();
      if (attempt < maxAttempts - 1) {
        await sleep(300 * (attempt + 1));
      }
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

const tryVerify = async (publicKey: string, token: string): Promise<ValidTokenType | null> => {
  try {
    var decoded = await verify(publicKey, token, {
      clientType: UIClientTypes.dashboard,
      algorithms: ['RS256'],
      ignoreExpiration: false,
    });
    if (!decoded || isString(decoded)) {
      console.warn('jwt response was invalid, an object was expected', decoded);
      return null;
    } else {
      const jwtValues = decoded as LoginJwtValue;

      return { value: token, decoded: jwtValues };
    }
  } catch (err) {
    console.warn('token verification failed:', err);
    return null;
  }
};

export type VerifyTokenOutcome =
  | { kind: 'ok'; result: ValidTokenType }
  | { kind: 'invalid' }
  | { kind: 'network'; message: string };

/**
 * Verifies a dashboard JWT with explicit outcome for bootstrap UX (network vs invalid).
 * Retries public key fetch a few times on transport failures; does not conflate invalid JWT with network.
 */
export const verifyTokenWithOutcome = async (id_token: string): Promise<VerifyTokenOutcome> => {
  let pubKey: string;
  try {
    pubKey = await getUiApiPublicKeyWithRetries(3);
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    console.warn('[auth] verifyTokenWithOutcome: public key unavailable after retries', e);
    return { kind: 'network', message };
  }

  let verified = await tryVerify(pubKey, id_token);
  if (!verified) {
    clearUiApiPublicKeyCache();
    try {
      pubKey = await getUiApiPublicKey(true);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      console.warn('[auth] verifyTokenWithOutcome: public key refetch after verify miss failed', e);
      return { kind: 'network', message };
    }
    verified = await tryVerify(pubKey, id_token);
  }

  if (verified) {
    return { kind: 'ok', result: verified };
  }
  return { kind: 'invalid' };
};

export const verifyToken = async (id_token: string): Promise<ValidTokenType | null> => {
  if (!Config.tbfe2301ResilientDashboardAuth) {
    try {
      const pubKey = await fetch(`${Config.uiApi}/publickey`).then(res => res.json());
      if (!pubKey || typeof pubKey.key !== 'string') {
        console.warn('[auth] verifyToken: legacy publickey response invalid');
        return null;
      }
      return await tryVerify(pubKey.key, id_token);
    } catch (err) {
      console.warn('[auth] verifyToken: legacy publickey fetch or verify failed', err);
      return null;
    }
  }

  const pubKey = await getUiApiPublicKey();
  let result = await tryVerify(pubKey, id_token);
  if (!result) {
    clearUiApiPublicKeyCache();
    const pubKey2 = await getUiApiPublicKey();
    result = await tryVerify(pubKey2, id_token);
  }
  return result;
};

export const extractApiTokenFromJwt = (jwtToken: string): string | null => {
  try {
    if (!jwtToken || jwtToken.split('.').length !== 3) {
      return null;
    }
    const decoded = jwt.decode(jwtToken) as any;
    return decoded && decoded.token ? decoded.token : null;
  } catch (error) {
    console.warn('Failed to extract API token from JWT:', error);
    return null;
  }
};

export const tokenCheck = async (apiToken: string): Promise<boolean> => {
  try {
    const api = ApiFactory(Config.tradingApi, apiToken);
    const response = await api.auth.checkToken();
    // Success can come as ResponseCode 0 or Payload === 'VALID'
    return response.responseCode === 0 || (typeof response.payload === 'string' && response.payload === 'VALID');
  } catch (err) {
    console.warn('TokenCheck failed:', err);
    return false;
  }
};

export const validateToken = async (id_token: string) => {
  // const pubKey = await fetch(`${Config.uiApi}/publickey`).then(res => res.json());
  const tokenResult = await fetch(`${Config.uiApi}/token`, {
    headers: { Authorization: id_token },
    credentials: 'include',
  }).then(res => res.json());
  const verifyRes = await tryVerify(tokenResult.key, tokenResult.accessToken);
  return verifyRes
    ? {
        ...verifyRes,
      }
    : undefined;
};
